import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Users, ArrowRight } from "lucide-react";

function GoogleIcon({ className, ...props }: { className?: string } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" {...props}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function AuthPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [form, setForm] = useState({ username: "", password: "", displayName: "", email: "" });
  const { login, register, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: googleAvailable } = useQuery<{ available: boolean }>({
    queryKey: ["/api/auth/google/available"],
    queryFn: async () => {
      const res = await fetch("/api/auth/google/available", { credentials: "include" });
      if (!res.ok) return { available: false };
      return res.json();
    },
  });

  if (isAuthenticated) {
    navigate("/community");
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "login") {
      login.mutate(
        { username: form.username, password: form.password },
        {
          onSuccess: () => navigate("/community"),
          onError: (err) => toast({ title: "Login failed", description: err.message, variant: "destructive" }),
        }
      );
    } else {
      register.mutate(
        { username: form.username, password: form.password, displayName: form.displayName, email: form.email || undefined },
        {
          onSuccess: () => navigate("/community"),
          onError: (err) => toast({ title: "Registration failed", description: err.message, variant: "destructive" }),
        }
      );
    }
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="text-primary" size={32} aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-display font-bold mb-2">
              {mode === "login" ? "Welcome Back" : "Join the Community"}
            </h1>
            <p className="text-muted-foreground">
              {mode === "login"
                ? "Sign in to access the community forum, events, and resources."
                : "Create a free account to connect with other founders and leaders."}
            </p>
          </div>

          <Card className="border-white/10 bg-card">
            <CardContent className="p-8">
              {googleAvailable?.available && (
                <>
                  <a
                    href="/api/auth/google"
                    className="w-full h-12 flex items-center justify-center gap-3 rounded-md border border-white/10 bg-background hover:bg-white/5 transition-colors font-medium text-sm"
                    data-testid="button-google-login"
                  >
                    <GoogleIcon aria-hidden="true" />
                    Sign in with Google
                  </a>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-3 text-muted-foreground">or continue with</span>
                    </div>
                  </div>
                </>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" aria-label={mode === "login" ? "Sign in form" : "Registration form"}>
                {(login.isError || register.isError) && (
                  <div role="alert" className="sr-only">
                    {mode === "login" ? "Login failed" : "Registration failed"}
                  </div>
                )}
                {mode === "register" && (
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Full Name</Label>
                    <Input
                      id="displayName"
                      data-testid="input-display-name"
                      placeholder="Matt Rebeiro"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      className="bg-background border-white/10"
                      required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="input-username"
                    placeholder="Enter your username"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="bg-background border-white/10"
                    required
                  />
                </div>

                {mode === "register" && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {mode === "login" && (
                      <Link href="/forgot-password" className="text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="link-forgot-password">
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <Input
                    id="password"
                    data-testid="input-password"
                    type="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-background border-white/10"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 font-semibold"
                  data-testid="button-auth-submit"
                  disabled={login.isPending || register.isPending}
                  aria-busy={login.isPending || register.isPending}
                >
                  {login.isPending || register.isPending
                    ? "Please wait..."
                    : mode === "login"
                      ? "Sign In"
                      : "Create Account"}
                  <ArrowRight className="ml-2" size={18} aria-hidden="true" />
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="button-toggle-auth-mode"
                >
                  {mode === "login" ? "Don't have an account? Sign up free" : "Already have an account? Sign in"}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </>
  );
}
